import React, { useState } from 'react';
import { Platform, Alert, Pressable, ActivityIndicator, Text, View } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import { authenticateWithPasskey } from '@utils/passkeyUtils';
import { isWebAuthnSupported } from '@api/passkeyApi';
import { useTranslation } from 'react-i18next';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { getPillButtonStyle } from '@stylesheet';

interface PasskeyLoginButtonProps {
  email: string;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
}

export const PasskeyLoginButton: React.FC<PasskeyLoginButtonProps> = ({
  email,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const theme = themeSelector();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const disabled = isAuthenticating || !email?.trim();

  const handleLogin = async () => {
    if (!email?.trim()) {
      const errorMsg = t('passkeyEmailRequired');
      if (onError) onError(errorMsg);
      else Alert.alert(t('Error'), errorMsg);
      return;
    }

    if (!isWebAuthnSupported()) {
      const errorMsg =
        Platform.OS === 'web'
          ? t('Passkeys are not supported in this browser.')
          : t('passkeyNativeUnsupported');
      if (onError) onError(errorMsg);
      else Alert.alert(t('Error'), errorMsg);
      return;
    }

    setIsAuthenticating(true);
    try {
      const result = await authenticateWithPasskey(email.trim());
      onSuccess?.(result);
    } catch (error: any) {
      const errorMsg = error.message || t('passkeyAuthFailed');
      if (onError) onError(errorMsg);
      else Alert.alert(t('Error'), errorMsg);
    } finally {
      setIsAuthenticating(false);
    }
  };

  if (!isWebAuthnSupported() || Platform.OS !== 'web') {
    return null;
  }

  return (
    <Pressable
      onPress={handleLogin}
      disabled={disabled}
      style={[
        getPillButtonStyle(theme, 'primary'),
        {
          width: '100%',
          marginTop: 4,
          opacity: disabled ? 0.6 : 1,
        },
      ]}
      accessibilityRole="button"
      accessibilityLabel={t('Sign in with Passkey')}
    >
      {isAuthenticating ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <ActivityIndicator size="small" color="#fff" />
          <Text
            style={{
              color: '#fff',
              fontSize: responsiveFontSize(13),
              fontWeight: '500',
            }}
          >
            {t('Authenticating...')}
          </Text>
        </View>
      ) : (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <FontAwesome6 name="fingerprint" size={responsiveFontSize(18)} color="#fff" />
          <Text
            style={{
              color: '#fff',
              fontSize: responsiveFontSize(13),
              fontWeight: '500',
            }}
          >
            {t('Sign in with Passkey')}
          </Text>
        </View>
      )}
    </Pressable>
  );
};
